import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase, readableError } from '../../lib/supabase'
import { useSession } from '../../lib/session'
import { Shell } from '../../components/Shell'
import { Sill, Spinner, Check, initials, sectionLabel } from '../../components/ui'
import SignatureGate from '../../components/SignatureGate'

/** What a named co-writer sees. Their signature ties to their own account. */
export default function CoSign() {
  const { id } = useParams()
  const { user } = useSession()
  const nav = useNavigate()
  const [song, setSong] = useState(null)
  const [ready, setReady] = useState(false)
  const [cowriters, setCowriters] = useState([])
  const [sections, setSections] = useState([])
  const [err, setErr] = useState(null)

  const load = useCallback(async () => {
    const [{ data: s }, { data: cw }, { data: sec }] = await Promise.all([
      supabase.from('songs').select('*').eq('id', id).maybeSingle(),
      supabase.from('song_cowriters').select('*').eq('song_id', id).order('is_primary', { ascending: false }),
      supabase.from('song_sections').select('*').eq('song_id', id).order('order_position'),
    ])
    setSong(s ?? null); setCowriters(cw ?? []); setSections(sec ?? []); setReady(true)
  }, [id])

  useEffect(() => { load() }, [load])

  if (!ready) return <Shell><Spinner /></Shell>
  if (!song) return (
    <Shell back="/songs" title="Your songs">
      <h2 className="q">We can't find that song.</h2>
      <p className="sub">It may have been removed, or you're not named on it.</p>
    </Shell>
  )

  const mine = cowriters.find((c) => c.user_id === user.id)
  const total = cowriters.reduce((a, c) => a + Number(c.split_percentage || 0), 0)

  if (!mine) return (
    <Shell back="/songs" title="Your songs">
      <p className="sub">You're not named on this song.</p>
    </Shell>
  )

  if (mine.signed) return (
    <Shell back="/songs" title="Your songs">
      <div className="done-ic"><Check size={26} w={2.4} /></div>
      <h2 className="q center">Signed.</h2>
      <p className="sub center">
        Your {mine.split_percentage}% of “{song.title}” is on the record, timestamped
        {' '}{new Date(mine.signed_at).toLocaleString()}.
      </p>
      <p className="small center">
        {cowriters.every((c) => c.signed)
          ? 'Every writer has signed — it can go to the church now.'
          : 'Waiting on the others before it can move.'}
      </p>
      <button className="btn btn-quiet" onClick={() => nav('/me')}>Done</button>
    </Shell>
  )

  return (
    <Shell back="/songs" title="Your songs">
      <div className="eyebrow">You've been named as a co-writer</div>
      <h2 className="q">{song.title}</h2>
      <p className="sub">
        Check the share below is what you agreed. Once you sign, it's fixed — nobody can move it.
      </p>
      <Sill />

      {cowriters.map((c) => (
        <div key={c.id} className={`cosign${c.signed ? ' signed' : ''}`}>
          <div className="ava">{initials(c.cowriter_name)}</div>
          <div className="who">
            <div className="n">{c.cowriter_name}{c.id === mine.id ? ' — you' : ''}</div>
            <div className="st">{c.split_percentage}% · {c.signed ? 'signed' : 'not yet signed'}</div>
          </div>
          {c.signed && <span className="badge">Signed</span>}
        </div>
      ))}
      <div className="splittotal">
        <span className="small">Total allocated</span>
        <span className={`amt ${total === 100 ? 'ok' : 'bad'}`}>{total}%</span>
      </div>

      {sections.length > 0 && <>
        <div className="hr" />
        <div className="eyebrow">The words</div>
        {sections.map((s) => (
          <div key={s.id} style={{ marginBottom: 14 }}>
            <div className="label">{sectionLabel(s)}</div>
            <p style={{ whiteSpace: 'pre-wrap', margin: 0, fontSize: 14 }}>{s.lyrics}</p>
          </div>
        ))}
      </>}

      <div className="hr" />
      <SignatureGate
        cowriter={mine}
        songTitle={song.title}
        onSigned={load}
      />
      {err && <p className="err">{err}</p>}
    </Shell>
  )
}
